import { useEffect, useState, useCallback } from 'react';
import {
  Package, ShareNetwork, Key, EnvelopeSimple, Buildings, CalendarBlank,
  IdentificationBadge, Copy, PencilSimple, Crown, UsersThree,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { itApi, STAFF_STATUS_META, ASSET_STATUS_META, EMAIL_STATUS_META } from './itApi';
import { ResourceCard, Credential } from './StaffProfile';
import { formatDate } from '@/utils/datetime';

const fmtDate = (s) => (s ? formatDate(s) : '—');
const Empty = ({ label }) => <p className="text-xs text-zinc-400 py-6 text-center">{label}</p>;

const copyText = async (v) => {
  try { await navigator.clipboard.writeText(v); toast.success('コピーしました'); }
  catch { toast.error('コピーに失敗しました'); }
};

// 1つのライセンス座席（チームプラン対応: 本人がメール/パスワードを登録・更新できる）
function MyLicenseSeat({ seat: s, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const startEdit = () => { setEmail(s.seat_login_email || s.login_email || ''); setPassword(''); setEditing(true); };

  const save = async () => {
    setBusy(true);
    try {
      const body = { login_email: email || null };
      if (password) body.password = password;
      await itApi.updateMySeatCredentials(s.id, body);
      toast.success('認証情報を保存しました');
      setEditing(false); setPassword('');
      onChanged?.();
    } catch (err) { toast.error(err?.response?.data?.detail || '保存に失敗しました'); }
    finally { setBusy(false); }
  };

  const shownEmail = s.seat_login_email || s.login_email;
  return (
    <li className="rounded-lg border border-zinc-100 px-3 py-2 text-sm" data-testid={`myit-license-${s.id}`}>
      <div className="flex items-start justify-between gap-2">
        <span className="font-semibold text-zinc-800 leading-snug min-w-0">{s.license_name || '—'}</span>
        <button onClick={editing ? () => setEditing(false) : startEdit} data-testid={`myit-license-${s.id}-edit`}
          className="text-zinc-400 hover:text-teal-700 p-0.5 shrink-0" title="自分のログインメール・パスワードを登録/変更">
          <PencilSimple size={14} />
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-1 mt-1">
        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-[10px] px-1.5 py-0">{s.license_type_label || '—'}</Badge>
        {s.is_admin && (
          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px] px-1.5 py-0" data-testid={`myit-license-${s.id}-admin`}>
            <Crown size={10} weight="fill" className="mr-0.5" /> 管理者
          </Badge>
        )}
      </div>
      <div className="mt-1.5 flex items-center justify-between gap-2 flex-wrap">
        {shownEmail ? (
          <span className="inline-flex items-center gap-1 text-[11px] text-zinc-500 min-w-0">
            <EnvelopeSimple size={12} />
            <span className="font-mono truncate">{shownEmail}</span>
            <button onClick={() => copyText(shownEmail)} data-testid={`myit-license-${s.id}-email-copy`} className="text-zinc-400 hover:text-teal-700" title="コピー"><Copy size={12} /></button>
          </span>
        ) : <span className="text-[11px] text-zinc-400">ログインメール未設定</span>}
        <Credential hasPassword={s.seat_has_password} emptyLabel="パスワード未登録"
          onReveal={() => itApi.revealMySeat(s.id).then((r) => r.password)}
          testid={`myit-license-${s.id}`} />
      </div>
      {editing && (
        <div className="mt-2 rounded-lg bg-zinc-50 border border-zinc-200 p-2 space-y-2" data-testid={`myit-license-${s.id}-edit-form`}>
          <p className="text-[10px] text-zinc-500">この座席（あなた専用）のログイン情報を登録します。パスワードは暗号化して保存され、表示は監査ログに記録されます。</p>
          <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ログインメール"
            data-testid={`myit-license-${s.id}-email-input`} className="h-9 text-sm bg-white" />
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder={s.seat_has_password ? '新しいパスワード（変更する場合のみ）' : 'パスワード'}
            data-testid={`myit-license-${s.id}-password-input`} className="h-9 text-sm bg-white" />
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="outline" onClick={() => setEditing(false)} className="h-8">キャンセル</Button>
            <Button size="sm" disabled={busy} onClick={save} data-testid={`myit-license-${s.id}-save`} className="h-8 bg-teal-700 hover:bg-teal-800">保存</Button>
          </div>
        </div>
      )}
    </li>
  );
}

// マイIT — self-service view: each user sees the IT resources assigned to THEM.
// Reveals go through /my-it endpoints which enforce ownership + audit logging.
export default function MyIT() {
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    itApi.myIt().then(setP).catch(() => setP(null)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="bg-white rounded-xl border border-zinc-200 p-12 text-center text-sm text-zinc-400" data-testid="myit-loading">読み込み中…</div>;
  }

  if (!p || !p.employee) {
    return (
      <div className="bg-white rounded-xl border border-zinc-200 p-12 text-center" data-testid="myit-no-employee">
        <IdentificationBadge size={44} weight="duotone" className="text-zinc-300 mx-auto mb-3" />
        <p className="text-sm font-semibold text-zinc-600">アカウントに社員情報が紐付いていません</p>
        <p className="text-xs text-zinc-400 mt-2 max-w-md mx-auto leading-relaxed">
          管理者に「設定 → ユーザー管理」であなたのアカウントと社員を紐付けてもらうと、割り当てられたIT資産・ライセンス・メールがここに表示されます。
        </p>
      </div>
    );
  }

  const emp = p.employee;
  const sm = STAFF_STATUS_META[emp.status] || STAFF_STATUS_META.active;

  return (
    <div className="space-y-5" data-testid="myit-page">
      {/* Header */}
      <div className="bg-white border border-zinc-200 rounded-xl shadow-sm p-5 md:p-6">
        <div className="flex items-center gap-4 min-w-0">
          <div className="flex items-center justify-center w-14 h-14 rounded-full bg-teal-700 text-white text-lg font-bold shrink-0 shadow-sm">
            {(emp.name || '?').replace(/\s/g, '').slice(0, 2)}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-semibold tracking-tight text-zinc-900 truncate" data-testid="myit-name">{emp.name}</h1>
              <Badge variant="outline" className={sm.cls}>{sm.label}</Badge>
            </div>
            <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500 flex-wrap">
              <span className="inline-flex items-center gap-1"><Buildings size={14} /> {emp.department_name || '部署未設定'}</span>
              {emp.code && <span className="inline-flex items-center gap-1"><IdentificationBadge size={14} /> {emp.code}</span>}
              <span className="inline-flex items-center gap-1"><CalendarBlank size={14} /> 入社 {fmtDate(emp.join_date)}</span>
            </div>
            <p className="text-[11px] text-zinc-400 mt-1.5">あなたに割り当てられたIT資産・ライセンス・メールアカウントの一覧です</p>
          </div>
        </div>
      </div>

      {/* Bento grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
        <ResourceCard icon={Package} title="貸与資産" count={p.assets.length} accent="bg-blue-50 text-blue-600" testid="myit-card-assets" bodyClass="max-h-[320px] overflow-y-auto pr-1">
          {p.assets.length === 0 ? <Empty label="割当資産はありません" /> : (
            <ul className="space-y-2">
              {p.assets.map((a) => {
                const asm = ASSET_STATUS_META[a.status] || ASSET_STATUS_META.in_stock;
                return (
                  <li key={a.id} className="flex items-center justify-between gap-2 text-sm rounded-lg border border-zinc-100 px-3 py-2" data-testid={`myit-asset-${a.id}`}>
                    <span className="min-w-0">
                      <span className="font-medium text-zinc-800 truncate block">{a.name}</span>
                      <span className="text-[11px] text-zinc-400 font-mono">{a.asset_tag || '—'}{a.ip_address ? ` ・ ${a.ip_address}` : ''}</span>
                    </span>
                    <Badge variant="outline" className={`${asm.cls} shrink-0`}>{asm.label}</Badge>
                  </li>
                );
              })}
            </ul>
          )}
        </ResourceCard>

        <ResourceCard icon={ShareNetwork} title="IPアドレス" count={p.ips.length} accent="bg-violet-50 text-violet-600" testid="myit-card-ips" bodyClass="max-h-[320px] overflow-y-auto pr-1">
          {p.ips.length === 0 ? <Empty label="割当IPはありません" /> : (
            <ul className="space-y-2">
              {p.ips.map((ip) => (
                <li key={ip.id} className="flex items-center justify-between gap-2 text-sm rounded-lg border border-zinc-100 px-3 py-2">
                  <span className="font-mono text-zinc-800">{ip.ip_address}</span>
                  <span className="text-[11px] text-zinc-400 truncate">{ip.device_name || ip.purpose || '—'}</span>
                </li>
              ))}
            </ul>
          )}
        </ResourceCard>

        <ResourceCard icon={Key} title="ライセンス" count={p.license_seats.length} accent="bg-amber-50 text-amber-600" testid="myit-card-licenses" bodyClass="max-h-[500px] overflow-y-auto pr-1">
          {p.license_seats.length === 0 ? <Empty label="割当ライセンスはありません" /> : (
            <ul className="space-y-2">
              {p.license_seats.map((s) => <MyLicenseSeat key={s.id} seat={s} onChanged={load} />)}
            </ul>
          )}
        </ResourceCard>

        <ResourceCard icon={UsersThree} title="共有ライセンス" count={(p.shared_licenses || []).length} accent="bg-teal-50 text-teal-600" testid="myit-card-shared-licenses" bodyClass="max-h-[500px] overflow-y-auto pr-1">
          {(p.shared_licenses || []).length === 0 ? <Empty label="共有されたライセンスはありません" /> : (
            <ul className="space-y-2">
              {p.shared_licenses.map((l) => (
                <li key={l.id} className="rounded-lg border border-zinc-100 px-3 py-2 text-sm" data-testid={`myit-shared-license-${l.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-semibold text-zinc-800 leading-snug min-w-0" data-no-translate>{l.name || '—'}</span>
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-[10px] px-1.5 py-0 shrink-0">{l.type_label || '—'}</Badge>
                  </div>
                  {l.shared_via_department && (
                    <p className="text-[10px] text-zinc-400 mt-0.5">部署への共有で利用できます</p>
                  )}
                  {l.login_email && (
                    <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-zinc-600 min-w-0">
                      <EnvelopeSimple size={12} className="shrink-0" />
                      <span className="font-mono truncate" data-no-translate>{l.login_email}</span>
                      <button onClick={() => copyText(l.login_email)} data-testid={`myit-shared-license-${l.id}-email-copy`} className="text-zinc-400 hover:text-teal-700 shrink-0" title="コピー"><Copy size={12} /></button>
                    </div>
                  )}
                  <div className="mt-1.5">
                    <Credential hasPassword={l.has_password} onReveal={() => itApi.revealLicense(l.id).then((r) => r.password)} testid={`myit-shared-license-${l.id}`} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </ResourceCard>

        <ResourceCard icon={EnvelopeSimple} title="メールアカウント" count={p.emails.length} accent="bg-emerald-50 text-emerald-600" testid="myit-card-emails" bodyClass="max-h-[320px] overflow-y-auto pr-1">
          {p.emails.length === 0 ? <Empty label="割当メールはありません" /> : (
            <ul className="space-y-2">
              {p.emails.map((em) => {
                const esm = EMAIL_STATUS_META[em.status] || EMAIL_STATUS_META.active;
                return (
                  <li key={em.id} className="rounded-lg border border-zinc-100 px-3 py-2 text-sm" data-testid={`myit-email-${em.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="inline-flex items-center gap-1 text-zinc-800 min-w-0">
                        <span className="truncate">{em.email}</span>
                        <button onClick={() => copyText(em.email)} data-testid={`myit-email-${em.id}-copy`} className="text-zinc-400 hover:text-teal-700 shrink-0" title="コピー"><Copy size={12} /></button>
                      </span>
                      <Badge variant="outline" className={`${esm.cls} shrink-0`}>{esm.label}</Badge>
                    </div>
                    <div className="mt-1.5">
                      <Credential hasPassword={em.has_password} onReveal={() => itApi.revealMyEmail(em.id).then((r) => r.password)} testid={`myit-email-${em.id}`} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </ResourceCard>
      </div>

      <p className="text-[11px] text-zinc-400 px-1">パスワードの表示はすべて記録（監査ログ）されます。IPアドレスはセキュリティのため一部のみ表示されます。</p>
    </div>
  );
}
